import React, { useEffect, useRef, useState } from 'react';

const DirectorSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [counts, setCounts] = useState({ healing: 50, biocompat: 100, gen: 3 });

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        // Animate from 0 to target values
        let start = null;
        const duration = 2200;
        const animate = (ts) => {
          if (!start) start = ts;
          const progress = Math.min((ts - start) / duration, 1);
          const ease = 1 - Math.pow(1 - progress, 3); // cubic ease-out
          setCounts({
            healing: Math.round(ease * 50),
            biocompat: Math.round(ease * 100),
            gen: Math.min(Math.round(ease * 3), 3),
          });
          if (progress < 1) requestAnimationFrame(animate);
        };
        requestAnimationFrame(animate);
      }
    }, { threshold: 0.3 });
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const ordinal = (n) => {
    if (n === 0) return '3rd'; // fallback — never show "0rd"
    if (n === 1) return '1st';
    if (n === 2) return '2nd';
    if (n === 3) return '3rd';
    return `${n}th`;
  };

  return (
    <section ref={sectionRef} data-testid="discover-section" className="relative py-16 sm:py-20 md:py-24 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 overflow-hidden">
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/8 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/8 rounded-full blur-3xl" />
      </div>
      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10 sm:mb-14" style={{ opacity: isVisible ? 1 : 0, transform: `translateY(${isVisible ? 0 : 24}px)`, transition: 'all 0.8s ease-out' }}>
            <div className="mb-5 flex items-center justify-center gap-4">
              <span className="h-px w-10 bg-gradient-to-r from-transparent to-blue-400/70" />
              <span className="font-display text-xs font-bold uppercase tracking-[0.35em] text-blue-400">Mechanism of Action</span>
              <span className="h-px w-10 bg-gradient-to-l from-transparent to-blue-400/70" />
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white mb-4 px-4">
              THE <span className="text-blue-400">REVOLUTION</span> IN BONE REGENERATION
            </h2>
            <p className="text-base sm:text-lg text-slate-400 max-w-3xl mx-auto px-4 leading-relaxed">
              Once implanted, the ~65%-porous hydroxyapatite matrix absorbs blood and body fluids — carrying the patient's own growth factors into the scaffold to stimulate and regulate bone reconstruction. Its bioactive surface bonds directly to bone.
            </p>
          </div>

          {/* Hardcoded counters — values always visible for crawlers */}
          <div className="grid grid-cols-3 gap-4 sm:gap-6 max-w-2xl mx-auto mb-12 sm:mb-14">
            <div className="text-center" data-testid="animated-counter-0">
              <div className="text-4xl sm:text-5xl lg:text-6xl font-black text-blue-400 mb-1" style={{ textShadow: '0 0 30px rgba(59, 130, 246, 0.4)' }}>
                <span>{isVisible ? counts.healing : 50}%</span>
              </div>
              <p className="text-xs sm:text-sm text-slate-400 font-semibold">Faster Healing</p>
            </div>
            <div className="text-center" data-testid="animated-counter-1">
              <div className="text-4xl sm:text-5xl lg:text-6xl font-black text-blue-400 mb-1" style={{ textShadow: '0 0 30px rgba(59, 130, 246, 0.4)' }}>
                <span>{isVisible ? counts.biocompat : 100}%</span>
              </div>
              <p className="text-xs sm:text-sm text-slate-400 font-semibold">Biocompatible</p>
            </div>
            <div className="text-center" data-testid="animated-counter-2">
              <div className="text-4xl sm:text-5xl lg:text-6xl font-black text-blue-400 mb-1" style={{ textShadow: '0 0 30px rgba(59, 130, 246, 0.4)' }}>
                <span>{isVisible ? ordinal(counts.gen) : '3rd'}</span>
              </div>
              <p className="text-xs sm:text-sm text-slate-400 font-semibold">Generation Biomaterial</p>
            </div>
          </div>

          {/* Videos — 2 col */}
          <div className="grid md:grid-cols-2 gap-6 mb-10 sm:mb-14 max-w-5xl mx-auto">
            {/* YouTube */}
            <div className="overflow-hidden border border-white/10 shadow-2xl shadow-blue-500/10">
              <div className="aspect-video">
                <iframe width="100%" height="100%" src="https://www.youtube.com/embed/OIqWfIFP9bY" title="FlexiOss Bone Regenerative Material — Product Overview" frameBorder="0" allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full" loading="lazy" />
              </div>
              <div className="bg-slate-800/50 px-5 py-3 border-t border-white/10">
                <p className="text-white font-bold text-sm">Product Overview</p>
                <p className="text-slate-400 text-xs">TriCurean composition, mechanism of action, clinical workflow</p>
              </div>
            </div>
            {/* Vimeo — Clinical Trauma */}
            <div className="overflow-hidden border border-white/10 shadow-2xl shadow-blue-500/10">
              <div className="aspect-video">
                <iframe src="https://player.vimeo.com/video/1180794050?title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479" width="100%" height="100%" frameBorder="0" allow="fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share" referrerPolicy="strict-origin-when-cross-origin" title="FlexiOss Orthopedic — Clinical Trauma Surgery" className="w-full h-full" loading="lazy" />
              </div>
              <div className="bg-slate-800/50 px-5 py-3 border-t border-white/10">
                <p className="text-white font-bold text-sm">Clinical Video — Orthopedic Trauma</p>
                <p className="text-slate-400 text-xs">Live surgical application of FlexiOss® in comminuted fracture repair</p>
              </div>
            </div>
          </div>

          {/* MoA info card */}
          <div className="max-w-4xl mx-auto" style={{ opacity: isVisible ? 1 : 0, transform: `translateY(${isVisible ? 0 : 24}px)`, transition: 'all 0.8s ease-out 0.5s' }}>
            <div className="relative border-t border-white/10 pt-10">
              <h3 className="font-display text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight text-white">Biphasic Remodeling Pathway</h3>
              <p className="mt-4 max-w-3xl text-sm sm:text-base text-slate-300 leading-relaxed">
                Upon implantation, the curdlan polymer matrix absorbs blood and physiological fluids, forming a triple-helix scaffold that is neutral to macrophages and evokes no local inflammation. Embedded hydroxyapatite granules create a calcium- and phosphate-rich, bioactive microenvironment at the defect boundary. The composite then integrates with the surrounding bone and is gradually remodelled into natural bone tissue — eliminating the need for an implant-removal reoperation.
              </p>
              <div className="mt-10 grid gap-x-14 sm:grid-cols-2">
                {[
                  { bold: 'Bioactive Mineral Surface', text: 'The hydroxyapatite surface exchanges Ca²⁺ and PO₄³⁻ ions, promoting apatite formation and direct bonding at the bone-implant interface' },
                  { bold: 'Triple-Helix Scaffold', text: 'Curdlan gels into a collagen-like β-1,3-glucan conformation — non-inflammatory, form-stable and slowly resorbed' },
                  { bold: 'Antibiotic Carrier Matrix', text: 'High soaking capacity allows intra-operative loading with aminoglycoside antibiotics (e.g. gentamicin) and other sterile solutions, released in a biphasic, pore-dependent pattern (Belcarz 2013)' },
                  { bold: 'Documented Bone Formation', text: 'Clinical histology shows new collagen fibres, Haversian canals and lamellar bone at the implantation site' },
                ].map((f, i) => (
                  <div key={i} className="flex items-start gap-5 border-t border-white/10 py-6">
                    <span className="font-display text-sm font-bold tabular-nums text-blue-400/60">{String(i + 1).padStart(2, '0')}</span>
                    <div>
                      <p className="font-display text-base font-semibold tracking-tight text-white mb-1.5">{f.bold}</p>
                      <p className="text-slate-400 text-sm leading-relaxed">{f.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DirectorSection;
